﻿import { License as TransactionLicense } from 'apis/TransactionApi';
import { ApiCaller, FullfilledApiResponse } from 'apis/ApiCaller';
import { License as PartnerLicense } from 'apis/LicenseApi';
import { PaginatedResult } from 'apis/TransactionApi';
import { Success } from 'types';

/** Valid sort column names accepted by the Partner GetAllPaged endpoint. */
export type PartnerSortField =
    | 'partnerId'
    | 'partnerName'
    | 'contactEmail'
    | 'typePartnerId'
    | 'sectorId'
    | 'countryId'
    | 'countryName'
    | 'isActive'
    | 'dateCreate'
    | 'SupplierList'
    | 'StateId'
    | 'StateName'
    | 'LanguageID'
    | 'UserList'
    | 'EmailList'
    | 'CustomerID';

import { Country } from './CountryApi';

export interface Partner {
	Active?: boolean;
	ContactEmail: string;
	Country?: { Id: number; Name: string };
	CustomerID: { ID: string; OwnerID: number }[];
	CustomerIDString: string;
	DateCreate: Date;
	DisabledDetailsBtn: boolean;
	ID: number;
	Id: number;
	Name: string;
	OperatingCountries: Country[];
	PartnerDetails?: PartnerDetails;
	PartnerType: number;
	Sector?: ETypeSector | null;
	State?: { Id: number; Name: string };
	StateID: number;
	UserEmailList: string;
}

export interface PartnerDetails extends Partner {
	Active: boolean;
	Address1: string;
	Address2: string;
	City: string;
	Country: { Id: number; Name: string };
	CustomerIds: string;
	DateModified: Date;
	IconURL: string;
	Language: { Id: number; Name: string };
	OperatingCountries: { Id: number; Name: string }[];
	PartnerType: PartnerTypes;
	Phone: string;
	Policy: string;
	Sector: ETypeSector | null;
	SectorName: string;
	State: { Id: number; Name: string };
	StateID: number;
	Zip: string;
}

export enum ETypeSector {
	K12 = 1,
	Government,
	Commercial,
	HigherEducation
}

export enum PartnerTypes {
	SuperAdmin = 1,
	Distributor,
	Reseller,
	TestingCenter,
	Author,
	Instructor,
	Association,
	Student, // Not suppose to be here. Not a partner, but that's how it's setup.
}

export type ConsumerSettings = {
	Active: boolean;
	AdminAccess: boolean;
	AdvantageToolBaseURL: string;

	/** Environment-resolved global Blackboard LTI 1.3 Client ID (server config with hardcoded default). */
	BlackboardClientId: string;
	ConsumerKey: string;
	ConsumerSecret: string;
	Identifier: string;
	LTIAccess: boolean;
	PartnerId: number;
	StudentAccess: boolean;
	UserId: number;
}

export type LtiPracticeTest = {
	DeliverableControl: string;
	Name: string;
};

export type LtiProduct = {
	Color: string;
	IconURL: string;
	Id: number;
	Name: string;
	PracticeTests: LtiPracticeTest[];
};

export type LtiCourse = {
	DeliverableControl: string;
	HasCommonCartridge: boolean;
	Id: number;
	Name: string;
};

export type LtiCategory = {
	Courses: LtiCourse[];
	IconURL: string;
	Id: number;
	Name: string;
	Products: LtiProduct[];
};

export type IntegrationVendor = {
	ErrorMessage: string;
	IntegrationId: string;
	Key: string;
	OrderId: number;
	RedirectUrl: string;
	Secret: string;
	VendorId: number;
	VendorName: string;
}

export const partnerTypeNames = [
    'System Admin',
    'Distributor',
    'Reseller',
    'Testing Center',
    'Author',
    'Instructor',
    'Association',
    'Student'
];

export const partnerList = [
    { Id: PartnerTypes.TestingCenter, Name: 'Testing Center' },
    { Id: PartnerTypes.Distributor, Name: 'Distributor' },
    { Id: PartnerTypes.Reseller, Name: 'Reseller' },
    { Id: PartnerTypes.Association, Name: 'Association' }
];

export type SelectedPartner = {
    ID: number;
    Name: string;
};

/**
 * Represents an api handler for the React Partners page and hits the Areas\Api\Controllers\PartnersController.cs file.
 * */
export class PartnerApi {

    private static apiCaller = new ApiCaller('Partners');

    public static GetAll(partnerType: number): Promise<Partner[]> {

	    return this.apiCaller.GET<Partner[]>('GetAll', { partnerType });

    }

    public static GetAllPaged(
        partnerType: number,
        page = 1,
        pageSize = 25,
        sortField: PartnerSortField | '' = '',
        sortDir = '',
        searchTerm = ''
    ): Promise<PaginatedResult<Partner>> {

	    return this.apiCaller.GET<PaginatedResult<Partner>>('GetAllPaged', {
	        page,
	        pageSize,
	        partnerType,
	        searchTerm,
	        sortDir,
	        sortField
	    });

    }

    public static GetSelectedPartner(): Promise<SelectedPartner | null> {

	    return this.apiCaller.GET<SelectedPartner | null>('GetSelectedPartner', {});

    }

    public static SetSelectedPartner(partnerId: number): Promise<FullfilledApiResponse> {

	    return this.apiCaller.POST<FullfilledApiResponse>('SetSelectedPartner', { partnerId });

    }

    public static ClearSelectedPartner(): Promise<void> {

	    return this.apiCaller.POST('ClearSelectedPartner', {});

    }

    public static Get(partnerId: number, cultureId = 1): Promise<PartnerDetails> {

	    return this.apiCaller.GET<PartnerDetails>('Get', { cultureId, partnerId });

    }

    public static Create(partner: PartnerDetails): Promise<FullfilledApiResponse> {

	    return this.apiCaller.POST<FullfilledApiResponse>('Create', {
	        name: partner.Name,
	        active: partner.Active,
	        address1: partner.Address1,
	        address2: partner.Address2,
	        city: partner.City,
	        contactEmail: partner.ContactEmail,
	        countryId: partner.Country.Id,
	        customerIds: partner.CustomerIds,
	        iconURL: partner.IconURL,
	        languageId: partner.Language.Id,
	        operatingCountries: partner.OperatingCountries ? partner.OperatingCountries.map(countryObj => countryObj.Id).join(',') : [],
	        partnerType: partner.PartnerType,
	        phone: partner.Phone,
	        sector: partner.Sector,
	        stateId: partner.State.Id,
	        zip: partner.Zip
	    });

    }

    public static Update(partner: PartnerDetails): Promise<FullfilledApiResponse> {

	    return this.apiCaller.PUT<FullfilledApiResponse>('Update', {
	        name: partner.Name,
	        active: partner.Active,
	        address1: partner.Address1,
	        address2: partner.Address2,
	        city: partner.City,
	        contactEmail: partner.ContactEmail,
	        countryId: partner.Country.Id,
	        customerIds: partner.CustomerIds,
	        iconURL: partner.IconURL,
	        languageId: partner.Language.Id,
	        operatingCountries: partner.OperatingCountries ? partner.OperatingCountries.map(countryObj => countryObj.Id).join(',') : [],
	        partnerId: partner.ID,
	        phone: partner.Phone,
	        sector: partner.Sector,
	        stateId: partner.State.Id,
	        zip: partner.Zip
	    });

    }

    public static GetPartnerLicenses(partnerId: number, cultureId: number): Promise<PartnerLicense[]> {

	    return this.apiCaller.GET<PartnerLicense[]>('GetPartnerLicenses', { cultureId, partnerId });

    }

    public static AddLicensesToPartner(childPartnerId: number, licenseIds: number[], supplierIds: number[]): Promise<FullfilledApiResponse> {

	    return this.apiCaller.POST<FullfilledApiResponse>('AddLicenseToPartner', { childPartnerId, licenseIds, supplierIds });

    }

    public static DeleteLicenseFromPartner(childPartnerId: number, licenseId: number): Promise<FullfilledApiResponse> {

	    return this.apiCaller.POST<FullfilledApiResponse>('DeleteLicenseFromPartner', { childPartnerId, licenseId });

    }

    public static GetPartnerOrderHistory(cultureId = 1): Promise<TransactionLicense[]> {

	    return this.apiCaller.GET<TransactionLicense[]>('GetPartnerOrderHistory', { cultureId });

    }

    public static GetConsumerSettingsByPartnerId(partnerId: number): Promise<ConsumerSettings> {

	    return this.apiCaller.GET<ConsumerSettings>('GetConsumerSettingsByPartnerId', { partnerId });

    }

    public static CreateConsumserSettingsByPartnerId(partnerId: number): Promise<ConsumerSettings> {

	    return this.apiCaller.GET<ConsumerSettings>('CreateConsumserSettingsByPartnerId', { partnerId });

    }

    /**
     * Saves a manually-entered LTI deployment for a multi-tenant LMS (Blackboard, Schoology) —
     * completes provisioning of the lti.Platforms row for the managed partner. Both LMSes install a
     * GMetrix-owned app (Blackboard by Client ID, Schoology from the App Center), so the Integrations
     * page sends the Deployment ID alone and the client_id is resolved per environment server-side.
     * `clientId` is retained for a caller that must pin a per-installation Client ID; the backend
     * honours it for Schoology only. The partner is the session's SelectedPartner, resolved
     * server-side — never sent from the client.
     */
    public static SaveManualLtiPlatformDeployment(lms: 'blackboard' | 'schoology', deploymentId: string, clientId?: string): Promise<Success<void>> {

        return this.apiCaller.POST<Success<void>>('SaveManualLtiPlatformDeployment', { clientId, deploymentId, lms });

    }

    /**
     * Returns previously-saved manual LTI Deployment ID(s) for the managed partner (session's
     * SelectedPartner, resolved server-side) under the given LMS's config-driven client, so the UI
     * can pre-populate the form instead of always showing it blank. Now that Schoology deployments
     * also save under a config-driven client_id, this lookup can resolve them for both LMSes; the
     * Integrations page doesn't call it yet for either (both forms start blank).
     */
    public static GetManualLtiPlatformDeployments(lms: 'blackboard' | 'schoology'): Promise<string[]> {

        return this.apiCaller.GET<string[]>('GetManualLtiPlatformDeployments', { lms });

    }

    /**
     * Manually registers a platform for the managed partner (session's SelectedPartner, resolved
     * server-side — never sent from the client) under an LMS where the institution issues its own
     * Client ID (Canvas, Moodle). The four platform endpoint URLs are prefilled on the page from
     * the LMS type + site address but remain user-editable, so the entered values are what get
     * persisted (the backend validates they are absolute http/https URLs).
     */
    public static ProvisionManualLtiPlatform(
        lms: 'canvas' | 'moodle',
        clientId: string,
        deploymentId: string,
        platformUrl: string,
        issuer: string,
        authorizeUrl: string,
        accessTokenUrl: string,
        jwkSetUrl: string
    ): Promise<Success<void>> {

        return this.apiCaller.POST<Success<void>>('ProvisionManualLtiPlatform', { accessTokenUrl, authorizeUrl, clientId, deploymentId, issuer, jwkSetUrl, lms, platformUrl });

    }

    public static GetTestKeys(partnerId: number): Promise<LtiCategory[]> {

	    return this.apiCaller.GET<LtiCategory[]>('GetTestKeys', { partnerId });

    }

    public static GetCustomTestKeys(partnerId: number): Promise<LtiCategory[]> {

	    return this.apiCaller.GET<LtiCategory[]>('GetCustomTestKeys', { partnerId });

    }

    public static GetCourseKeys(partnerId: number): Promise<LtiCategory[]> {

	    return this.apiCaller.GET<LtiCategory[]>('GetCourseKeys', { partnerId });

    }

    public static GetDownloadCartridgeUrl(courseId: number, accessCode = ''): string {

	    const params = new URLSearchParams({ accessCode, courseId: String(courseId) });
	    return `/Api/Partners/DownloadCartridge?${params.toString()}`;

    }

    public static GetIntegrationVendors(partnerId: number, cultureId: number): Promise<IntegrationVendor[]> {

	    return this.apiCaller.GET<IntegrationVendor[]>('GetIntegrationVendors', { cultureId, partnerId });

    }

    public static GetJasperActiveTeacherCodeByCenter(partnerId: number): Promise<FullfilledApiResponse> {

	    return this.apiCaller.GET<FullfilledApiResponse>('GetJasperActiveTeacherCodeByCenter', { partnerId });

    }

    public static GetPartnerOperatingCountries(partnerId: number): Promise<Country[]> {

	    return this.apiCaller.GET<Country[]>('GetPartnerOperatingCountries', { partnerId });

    }

    public static GetOrderHistoryCountries(partnerId: number): Promise<Country[]> {

	    return this.apiCaller.GET<Country[]>('GetOrderHistoryCountries', { partnerId });

    }

    public static GetXEDIntegration(partnerId: number): Promise<FullfilledApiResponse> {

	    return this.apiCaller.GET<FullfilledApiResponse>('GetXEDIntegration', { partnerId });

    }

    public static MergePartners(sourcePartnerId: number, targetPartnerId: number): Promise<FullfilledApiResponse> {

	    return this.apiCaller.POST<FullfilledApiResponse>('Merge', { sourcePartnerId, targetPartnerId });

    }

    public static ReSyncIntegrationManagementServices(orderId: number): Promise<{ ExternalId: string; MsgSuccess: string; Successful: boolean; }> {

	    let baseUrl = window.location.host;

	    switch (true) {

	        // case baseUrl.includes("devmanage") || baseUrl.includes("localhost"):
	        //     baseUrl = "devmanagementservices";
	        //     break;
	        // case baseUrl.includes("stage"):
	        //     baseUrl = "stagemanagementservices";
	        //     break;
	        // case baseUrl.includes("preprod"):
	        //     baseUrl = "preprodmanagementservices";
	        //     break;
	        default:
	            baseUrl = 'managementservices';

	    }

	    return fetch(`https://${baseUrl}.gmetrix.net/api/AdminServices/TryResyncIntegration?Key=EDFE05D236D0&orderId=${orderId}`, {
	        headers: {
	            'Content-Type': 'application/json'
	        },
	        method: 'GET'
	    }).then(res => res.json()).catch(console.error);

    }

}
